import React, { useEffect, useRef, useState } from 'react';
import { Blacked } from "../../../../CRM/components/styles";
import { ReactComponent as ModalCloseIcon } from "../../../../assets/HomeTownIcon/ModalCloseIcon.svg";
// 불필요한 아이콘/컴포넌트 임포트 제거 (lint 경고 방지)
import styled from "styled-components";
import { DatePicker, Input, message, Select } from "antd";
import { ReactComponent as RightArrowIcon } from "../assets/RightArrowIcon.svg";
import { PreviewMonitor } from "./PreviewMonitor";
import { useAdvertiserADItemList } from "../../../hooks/Advertiser/CustomerCommercial/GetAdvertiserADItemList";
import API from "../../../../utils/api";
import { useToken } from "../../../../app/hooks";
import dayjs, { Dayjs } from "dayjs";
import { useAdvertiserADInfo } from "../../../hooks/Advertiser/CustomerCommercial/GetAdvertiserADInfo";
import { useMyInfo } from "../../../../CRM/hooks/GetMyInfo";
import { allowHiddenFeatureForUser } from "../../../../utils/env";
import { isLocalEnvironment } from "../../../../utils/env";

type FileItem = { name: string; fileType: number; url: string; urlId?: number };

export const ApplicationOneStep = ({
                                     setIsOneStepModal,
                                     setIsTwoStepModal,
                                     title,
                                     setTitle,
                                     startedAt,
                                     setStartedAt,
                                     endedAt,
                                     setEndedAt,
                                     itemId,
                                     setItemId,
                                     fileList,
                                     setFileList,
                                     days,
                                     setDays,
                                     setAdIdValue,
                                     setTotalPrice,
                                     mutateAdvertiserADList,
                                     totalPrice,
                                     adIdValue,
                                   }) => {
  // 유틸리티
  const { RangePicker } = DatePicker;
  const [isPreviewModal, setIsPreviewModal] = React.useState(false);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [deleteFileList, setDeleteFileList] = React.useState<number[]>([]);
  const isLocal = isLocalEnvironment();

  // 데이터 조회
  const { advertiserADItemList } = useAdvertiserADItemList();
  const { advertiserADInfo } = useAdvertiserADInfo(adIdValue);
  const { data: token } = useToken();
  const { myInfo } = useMyInfo();
  const allowVideo = allowHiddenFeatureForUser(myInfo);
  const filteredAdvertiserADItemList = React.useMemo(() => {
    const list = advertiserADItemList ?? [];
    if (allowVideo) {
      return list;
    }
    return list.filter((item: any) => {
      const title = `${item?.title ?? ''}`.toLowerCase();
      const hasVideoInTitle = title.includes('동영상') || title.includes('video');
      return !(item?.itemId === 1 || item?.fileType === 1 || hasVideoInTitle);
    });
  }, [advertiserADItemList]);

  // === 무료 광고 제약 상수 ===
  const MAX_FREE_DAYS = 30;
  const isFree = myInfo?.premium === 0;

  // ---------- Helpers ----------
  const extractDates = (period?: string | null) => {
    if (!period) return;
    const regex = /(\d{4}-\d{2}-\d{2})~(\d{4}-\d{2}-\d{2})/;
    const matches = period.match(regex);
    if (matches) {
      const s = matches[1];
      const e = matches[2];
      const diff = dayjs(e).diff(dayjs(s), 'day') + 1;
      // 초기 데이터가 무료 제한을 초과하면 사용자에게 알림만 주고 그대로 세팅은 하지 않음(선택 시점에서 막힘)
      if (isFree && diff > MAX_FREE_DAYS) {
        message.error(`무료 광고의 최대 광고기간은 ${MAX_FREE_DAYS}일까지 가능합니다.`);
        return;
      }
      setStartedAt(s);
      setEndedAt(e);
      setDays(diff);
    }
  };

  const deepEqualFiles = (a: FileItem[] = [], b: FileItem[] = []) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i]?.url !== b[i]?.url || a[i]?.name !== b[i]?.name) return false;
    }
    return true;
  };

  // ---------- 초기 1회 세팅 가드 ----------
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!advertiserADInfo || initializedRef.current) return;

    // 제목/상품
    setTitle((prev) => (prev ?? '') || advertiserADInfo.title || '');
    setItemId((prev) => (prev ?? advertiserADInfo.itemId ?? null));

    // 기간
    if (advertiserADInfo.period) {
      const period = advertiserADInfo.period;
      const m = period.match(/(\d{4}-\d{2}-\d{2})~(\d{4}-\d{2}-\d{2})/);
      if (m) {
        const [, s, e] = m;
        const d = dayjs(e).diff(dayjs(s), 'day') + 1;
        if (isFree && d > MAX_FREE_DAYS) {
          message.error(`무료 광고의 최대 광고기간은 ${MAX_FREE_DAYS}일까지 가능합니다.`);
        } else {
          setStartedAt(s);
          setEndedAt(e);
          setDays(d);
        }
      } else if (period.includes('일')) {
        const d = Number(period.split('일')[0]) || 0;
        if (isFree && d > MAX_FREE_DAYS) {
          message.error(`무료 광고의 최대 광고기간은 ${MAX_FREE_DAYS}일까지 가능합니다.`);
          setDays(MAX_FREE_DAYS);
        } else {
          setDays(d);
        }
      }
    }

    // 파일(깊은 비교)
    const incoming = advertiserADInfo.fileList ?? [];
    setFileList((prev = []) => (deepEqualFiles(prev, incoming) ? prev : incoming));

    initializedRef.current = true;
  }, [advertiserADInfo, setTitle, setItemId, setStartedAt, setEndedAt, setDays, setFileList, isFree]);

  useEffect(() => {
    if (itemId === null || itemId === undefined || itemId === '') return;

    const allowedValues = new Set(
        filteredAdvertiserADItemList
            .map((item: any) => {
              const baseValue = Number(item?.itemId);
              if (!Number.isFinite(baseValue)) return null;
              return isFree ? (item?.itemId === 1 ? 3 : 4) : baseValue;
            })
            .filter((v) => v !== null),
    );

    if (allowedValues.size === 0) {
      setItemId(null);
      setFileList([]);
      return;
    }

    const normalizedItemId = typeof itemId === 'string' ? Number(itemId) : itemId;
    if (!Number.isFinite(normalizedItemId)) return;

    if (!allowedValues.has(normalizedItemId)) {
      setItemId(null);
      setFileList([]);
    }
  }, [filteredAdvertiserADItemList, isFree, itemId, setFileList, setItemId]);

  // ---------- 총액 계산 (값 변경시에만) ----------
  useEffect(() => {
    const price = filteredAdvertiserADItemList?.find((i) => i?.itemId === itemId)?.price ?? 0;
    const next = price * (days || 0);
    if (totalPrice !== next) setTotalPrice(next);
  }, [days, itemId, filteredAdvertiserADItemList, totalPrice, setTotalPrice]);

  // ---------- RangePicker 변경 ----------
  const handleDateChange = (dates: [Dayjs, Dayjs] | null) => {
    if (dates && dates.length === 2) {
      const diff = dates[1].diff(dates[0], 'day') + 1;
      if (isFree && diff > MAX_FREE_DAYS) {
        // 초과 선택 방지 + 에러 메시지
        message.error(`무료 광고의 최대 광고기간은 ${MAX_FREE_DAYS}일까지 가능합니다.`);
        return; // 기존 값 유지
      }
      console.log('//diff',diff);
      console.log('//dates',dates[0].format('YYYY-MM-DD'));
      console.log('//dates2',dates[1].format('YYYY-MM-DD'));
      setStartedAt(dates[0].format('YYYY-MM-DD'));
      setEndedAt(dates[1].format('YYYY-MM-DD'));
      setDays(diff);
    } else {
      setStartedAt(null);
      setEndedAt(null);
      setDays(0);
    }
  };

  // 무료일 때 종료일을 시작일+29일까지로 제한
  const disabledDate = (current: Dayjs) => {
    if (!isFree) return false;
    if (startedAt) {
      const start = dayjs(startedAt, 'YYYY-MM-DD');
      // 시작일 이후 29일 초과는 비활성화
      return current.isAfter(start.add(MAX_FREE_DAYS - 1, 'day'));
    }
    return false;
  };

  // ---------- 파일 업로드 ----------
  const UploadData = (file?: File, fileType: number = 0) => {
    if ((fileList?.length ?? 0) >= 1) {
      message.error('파일은 최대 1개만 허용합니다.');
      setFileInputKey(Date.now());
      return;
    }
    if (!file) {
      message.error('파일을 선택하세요');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    API.postImage('POST', '/content?type=user', '', formData)
        .then((res: any) => {
          if (res.message === 'success') {
            const url = res?.result?.url;
            const name = res?.result?.name;
            const serverFileType = Number(res?.result?.fileType);
            const resolvedFileType = Number.isFinite(serverFileType) ? serverFileType : fileType;
            message.success('파일이 추가되었습니다.');
            setFileList([{ name, fileType: resolvedFileType, url }]);
            setFileInputKey(Date.now());
          } else {
            message.error('파일이 잘못되었습니다');
          }
        })
        .catch((err: any) => {
          if (err?.message === 'IMAGE_SIZE_EXCEEDED') {
            setFileInputKey(Date.now());
            return;
          }
          if (err?.message === 'IMAGE_SIZE_EXCEEDED') {
            setFileInputKey(Date.now());
            return;
          }
          console.log('Upload Data ERROR', err);
          message.error('업로드 중 오류가 발생했습니다');
        });
  };

  // ---------- 등록/수정 ----------
  const guardFreeDays = () => {
    if (isFree && (days || 0) > MAX_FREE_DAYS) {
      message.error(`무료 광고의 최대 광고기간은 ${MAX_FREE_DAYS}일까지 가능합니다.`);
      return false;
    }
    return true;
  };

  const postAdvertiser = (type: '임시저장' | '다음') => {
    if (!guardFreeDays()) return;
    const shouldEdit = window.confirm(type === '임시저장' ? '임시저장하시겠습니까?' : `등록하시겠습니까?`);
    if (!shouldEdit) return;

    API.post('/advertiser/ad/list', (token as any) + '', {
      title,
      days,
      startedAt,
      endedAt,
      itemId,
      fileList,
    })
        .then((res: any) => {
          if (res.result.success) {
            if (type === '임시저장') {
              message.success(`임시저장하였습니다.`);
              mutateAdvertiserADList();
              setIsOneStepModal(false);
              setFileList([]);
            } else {
              message.success(`등록하였습니다.`);
              mutateAdvertiserADList();
              setIsTwoStepModal(true);
              setIsOneStepModal(false);
              setAdIdValue(res.result?.result?.adId || null);
              setFileList([]);
            }
          } else {
            message.error(res.result.message || `실패하였습니다.`);
          }
        })
        .catch((err: any) => {
          console.log('post error', err);
          message.error('실패하였습니다.');
        });
  };

  const putAdvertiser = (type: '임시저장' | '다음') => {
    if (!adIdValue) return;
    if (!guardFreeDays()) return;
    const shouldEdit = window.confirm(type === '임시저장' ? '임시저장하시겠습니까?' : `수정하시겠습니까?`);
    if (!shouldEdit) return;

    API.put(`/advertiser/ad/list/${adIdValue}`, (token as any) + '', {
      datas: {
        title,
        days,
        startedAt,
        endedAt,
        itemId,
        fileList,
        price: totalPrice,
        deleteFileList,
      },
    })
        .then((res: any) => {
          if (res.result.success) {
            if (type === '임시저장') {
              message.success(`임시저장하였습니다.`);
              mutateAdvertiserADList();
              setIsOneStepModal(false);
              setFileList([]);
            } else {
              message.success(`수정하였습니다.`);
              mutateAdvertiserADList();
              setIsTwoStepModal(true);
              setIsOneStepModal(false);
              setFileList([]);
            }
          } else {
            message.error(res.result.message || `실패하였습니다.`);
          }
        })
        .catch((err: any) => {
          console.log('put error', err);
          message.error('실패하였습니다.');
        });
  };

  // ---------- 파생 값 ----------
  const rangeValue: [Dayjs, Dayjs] | null =
      startedAt && endedAt
          ? [dayjs(startedAt, 'YYYY-MM-DD'), dayjs(endedAt, 'YYYY-MM-DD')]
          : null;

  const pricePerDay = filteredAdvertiserADItemList?.find((i) => i?.itemId === itemId)?.price ?? 0;
  const canNext = Array.isArray(fileList) && fileList.length > 0;


  return (
      <>
        <Blacked
            onClick={() => {
              setIsOneStepModal(false);
              setFileList([]);
            }}
        />
        <ModalWrapper className={"scroll-container"}>
          <ModalHeader>
            <div style={{ padding: 12 }} />
            <ModalTitle>광고신청</ModalTitle>
            <CloseIconSVG
                as={ModalCloseIcon}
                onClick={() => {
                  setIsOneStepModal(false);
                  setFileList([]);
                }}
            />
          </ModalHeader>

          <ProcessWrapper>
            <StepWrapper>
              <StepNumber Active>1</StepNumber>
              <StepTitle Active>광고내용입력</StepTitle>
            </StepWrapper>

            <ArrowSVG as={RightArrowIcon} />

            <StepWrapper>
              <StepNumber>2</StepNumber>
              <StepTitle>광고매체선택</StepTitle>
            </StepWrapper>

            <ArrowSVG as={RightArrowIcon} />

            <StepWrapper>
              <StepNumber>3</StepNumber>
              <StepTitle>신청완료 및 승인대기</StepTitle>
            </StepWrapper>
          </ProcessWrapper>

          <ContentsSection>
            <ContentsTitle>광고 상세 정보 입력</ContentsTitle>

            <Table>
              <tbody>
              <TableRow>
                <Th>
                  <ThLabel>광고명</ThLabel>
                </Th>
                <Td>
                  <InputText
                      type="text"
                      value={title ?? ''}
                      onChange={(e) => setTitle(e.target.value as string)}
                      placeholder="광고명을 입력하세요"
                  />
                </Td>
              </TableRow>

              <TableRow>
                <Th>
                  <ThLabel>광고기간</ThLabel>
                </Th>
                <Td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div>{`${days || 0}일 / `}</div>
                    <RangePicker
                        onChange={handleDateChange as any}
                        placeholder={['시작일', '종료일']}
                        format="YYYY-MM-DD"
                        value={rangeValue as any} // antd 타입 이슈 회피
                        // 무료일 때 30일 초과되는 종료일을 비활성화
                        disabledDate={disabledDate as any}
                    />
                  </div>
                </Td>
              </TableRow>

              <TableRow>
                <Th>
                  <ThLabel>광고상품</ThLabel>
                </Th>
                <Td>
                  {isFree ? (
                  <StyledSelect
                      value={itemId ?? undefined}
                      onChange={(value) => setItemId(value as number)}
                      placeholder={'선택'}
                  >
                        {filteredAdvertiserADItemList?.map((item: any) => (
                            <Select.Option key={item?.itemId} value={item?.itemId === 1 ? 3 : 4}>
                              {`${item?.title} - 일 0원`}
                            </Select.Option>
                        ))}
                      </StyledSelect>
                  ) : (
                  <StyledSelect
                      value={itemId ?? undefined}
                      onChange={(value) => setItemId(value as number)}
                      placeholder={'선택'}
                  >
                        {filteredAdvertiserADItemList?.map((item: any) => (
                            <Select.Option key={item?.itemId} value={item?.itemId}>
                              {`${item?.title} - 일 ${item?.price?.toLocaleString('en-US')}원`}
                            </Select.Option>
                        ))}
                      </StyledSelect>
                  )}
                </Td>
              </TableRow>

              <TableRow>
                <Th>
                  <ThLabel>광고료</ThLabel>
                </Th>
                <Td>
                  {isFree
                      ? '( 무료 )'
                      : `광고처 1곳당 ${(pricePerDay * (days || 0)).toLocaleString('en-US')}원`}
                </Td>
              </TableRow>

              <TableRow>
                <Th>
                  <ThLabel>
                    <div>
                      첨부파일
                      <div style={{ fontSize: 13 }}>
                        {((itemId === 1) || (itemId === 3)) && allowVideo ? '동영상' : '이미지'} 1개 가능
                      </div>
                      <div
                          onClick={() => setIsPreviewModal(true)}
                          style={{
                            color: '#530DAD',
                            textDecoration: 'underline',
                            marginTop: 10,
                            cursor: 'pointer',
                          }}
                      >
                        광고미리보기
                      </div>
                    </div>
                  </ThLabel>
                </Th>
                <Td>
                  <div>
                    <FileInput
                        accept={((itemId === 1) || (itemId === 3)) && allowVideo ? 'video/*' : 'image/*'}
                        key={fileInputKey}
                        type="file"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;

                          // 확장자 백업 체크 (일부 환경에서 file.type이 빈 문자열일 수 있음)
                          const ext = file.name.split('.').pop()?.toLowerCase();
                          const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic', 'heif'];
                          const videoExts = ['mp4', 'mov', 'm4v', 'avi', 'mkv', 'webm'];

                          const isImageByMime = file.type?.startsWith('image/');
                          const isVideoByMime = file.type?.startsWith('video/');
                          const isImageByExt = !!ext && imageExts.includes(ext);
                          const isVideoByExt = !!ext && videoExts.includes(ext);

                          // itemId === 1 => 동영상만 허용
                          if ((itemId === 1) || (itemId === 3)) {
                            if (!allowVideo && (isVideoByMime || isVideoByExt)) {
                              message.error('서버 환경에서는 동영상 업로드를 지원하지 않습니다.');
                              e.target.value = '';
                              setFileInputKey(Date.now());
                              return;
                            }
                            if (!(isVideoByMime || isVideoByExt)) {
                              alert('동영상 파일만 업로드할 수 있습니다. (예: mp4, mov, webm 등)');
                              // 인풋 리셋
                              e.target.value = '';
                              // 혹시 styled antd Input 캐싱 이슈가 있으면 key 갱신
                              setFileInputKey(Date.now());
                              return;
                            }
                          } else {
                            // 그 외 => 이미지 파일만 허용
                            if (!(isImageByMime || isImageByExt)) {
                              alert('이미지 파일만 업로드할 수 있습니다. (예: jpg, png, webp 등)');
                              e.target.value = '';
                              setFileInputKey(Date.now());
                              return;
                            }
                          }

                          // 통과 시 업로드
                          UploadData(file, 0);
                        }}
                    />


                    {fileList?.map((item: any) => (
                        <DataText key={item?.url}>
                          {item?.name}
                          <DeleteItemIconSVG
                              as={ModalCloseIcon}
                              onClick={() => {
                                if (item?.urlId) {
                                  setDeleteFileList((prev) => [...prev, item.urlId!]);
                                }
                                setFileList((prev) => {
                                  const list = [...(prev ?? [])];
                                  const idx = list.findIndex((i) => i.url === item?.url);
                                  if (idx !== -1) list.splice(idx, 1);
                                  return list;
                                });
                              }}
                          />
                        </DataText>
                    ))}
                  </div>
                </Td>
              </TableRow>
              </tbody>
            </Table>
          </ContentsSection>

          <ButtonWrapper>
            {/*<TempSaveButton*/}
            {/*    onClick={() => {*/}
            {/*      if (adIdValue) {*/}
            {/*        putAdvertiser('임시저장');*/}
            {/*      } else {*/}
            {/*        postAdvertiser('임시저장');*/}
            {/*      }*/}
            {/*    }}*/}
            {/*>*/}
            {/*  임시저장*/}
            {/*</TempSaveButton>*/}

            {canNext ? (
                <SignUpButton
                    onClick={() => {
                      if (adIdValue) {
                        putAdvertiser('다음');
                      } else {
                        postAdvertiser('다음');
                      }
                    }}
                >
                  다음
                </SignUpButton>
            ) : (
                <CancelButton
                    style={{
                      cursor: 'not-allowed',
                      backgroundColor: '#dad8d8',
                      color: '#7e7c7c',
                      border: '1px solid #7e7c7c',
                    }}
                >
                  다음
                </CancelButton>
            )}

            <CancelButton
                onClick={() => {
                  setIsOneStepModal(false);
                  setFileList([]);
                }}
            >
              취소
            </CancelButton>
          </ButtonWrapper>
        </ModalWrapper>

        {isPreviewModal && (
            <PreviewMonitor setIsPreviewModal={setIsPreviewModal} itemId={0} advertiserADList={fileList} />
        )}
      </>
  );
};

// ---------- styles ----------
const DataText = styled.div`
  font-weight: 400;
  font-size: 14px;
  font-family: Pretendard, sans-serif;
  line-height: 16.71px;
  color: #000000;
  margin-top: 8px;
  white-space: pre-wrap;
`;

const DeleteItemIconSVG = styled.svg`
  width: 12px;
  cursor: pointer;
  margin-left: 10px;
`;

const ModalWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-height: 800px;
  width: 880px;
  min-height: 411px;
  background-color: #fff;
  overflow-y: auto;
  border-radius: 8px;
  padding: 24px;
  z-index: 99999;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const CloseIconSVG = styled.svg`
  width: 24px;
  cursor: pointer;
`;

const ModalTitle = styled.div`
  font-family: Pretendard, sans-serif;
  font-weight: 700;
  font-size: 30px;
  line-height: 35.8px;
  color: #2e2559;
`;

const Table = styled.table`
  width: 100%;
  margin-top: 8px;
  border-collapse: collapse;
  border: 1px solid #ccc;
`;

const TableRow = styled.tr`
  min-height: 40px;
`;

const Th = styled.th`
  padding: 10px;
  text-align: left;
  width: 26%;
  border: 1px solid #ccc;
  background-color: #f4f2ff;
  vertical-align: middle;
`;

const ThLabel = styled.div`
  font-family: Pretendard, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 16.71px;
  color: #00000099;
  display: flex;
  gap: 2px;
`;

const Td = styled.td`
  padding: 10px;
  text-align: left;
  border: 1px solid #ccc;
`;

const InputText = styled(Input)`
  width: 100%;
  height: 28px;
  padding-left: 10px;
  border: 1px solid #2e2559;
  border-radius: 4px;
  display: flex;

  &::placeholder {
    color: #535353;
  }
`;

const FileInput = styled(Input)`
  width: 100%;
  padding: 2px;
  border: 1px solid #2e2559;
  border-radius: 4px;
  display: flex;
`;

const StyledSelect = styled(Select)`
  width: 100%;
  border: 1px solid #00000033;
  border-radius: 7px;
`;

const SignUpButton = styled.div`
  width: 100px;
  padding: 11px;
  background-color: #530dad;
  color: #fff;
  border: none;
  border-radius: 2px;
  cursor: pointer;
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;

  font-weight: 500;
  font-size: 15px;
  line-height: 17.9px;
  font-family: Pretendard, sans-serif;
`;

const ContentsTitle = styled.div`
  width: 100%;
  font-family: Pretendard, sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 21.48px;
  color: #000000cc;
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
`;

const ContentsSection = styled.div`
  width: 793px;
  background-color: #fafafc;
  padding: 40px 106.5px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%
;
  margin-top: 5px;
  gap: 6px;
`;

const TempSaveButton = styled.div`
  width: 100px;
  padding: 11px;
  color: #ff6c4b;
  border: 1px solid #ff6c4b;
  border-radius: 2px;
  cursor: pointer;
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;

  font-weight: 500;
  font-size: 15px;
  line-height: 17.9px;
  font-family: Pretendard, sans-serif;
`;

const CancelButton = styled.div`
  width: 100px;
  padding: 11px;
  border: 1px solid #000000;
  border-radius: 2px;
  cursor: pointer;
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;

  font-weight: 500;
  font-size: 15px;
  line-height: 17.9px;
  font-family: Pretendard, sans-serif;
`;

const ProcessWrapper = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  margin-top: 45.5px;
  margin-bottom: 67.5px;
`;

const ArrowSVG = styled.svg``;

const StepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const StepNumber = styled.div<{ Active?: boolean }>`
  background-color: ${(props) => (props.Active ? '#2e1155' : '#bfbfbf')};
  border-radius: 50px;
  width: 18px;
  height: 18px;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;

  font-size: 14px;
  font-weight: bold;
`;

const StepTitle = styled.div<{ Active?: boolean }>`
  font-family: Pretendard, sans-serif;
  font-weight: ${(props) => (props.Active ? '400' : '600')};
  font-size: 16px;
  line-height: 19.09px;
  color: ${(props) => (props.Active ? '#000000' : '#000000b2')};
`;
